// Video Search Bar Component
// Search bar for finding and adding videos to featured content

import React, { useState, useEffect } from 'react';
import { logger } from '../../../core';
import './VideoSearchBar.css';

export function VideoSearchBar({ onAddVideo }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Debounced search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/api/learn/videos?search=${encodeURIComponent(query.trim())}&limit=10`);
      const data = await response.json();

      if (data.success && data.videos) {
        setSearchResults(data.videos);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    } catch (error) {
      logger.error({
        tags: 'admin|featured|search|error',
        color1: 'red',
      }, 'Search error:', error);
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddVideo = (video) => {
    onAddVideo(video);
    setSearchQuery('');
    setShowResults(false);

    logger.log({
      tags: 'admin|featured|add',
      color1: 'green',
    }, `Added video to featured content: ${video.name || video.title}`);
  };

  const handleSearchFocus = () => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow clicking
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="video-search-bar">
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search for videos to feature..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
        />

        {isSearching && (
          <div className="search-loading">
            <i className="fa-solid fa-spinner fa-spin"></i>
          </div>
        )}
      </div>

      {/* Search results dropdown */}
      {showResults && searchResults.length > 0 && (
        <div className="search-results-dropdown">
          {searchResults.map((video) => (
            <button
              key={video.id}
              className="search-result-item"
              onClick={() => handleAddVideo(video)}
            >
              <div className="result-info">
                <span className="result-title">{video.name}</span>
                {video.shortDescription && (
                  <span className="result-description">
                    {video.shortDescription.length > 60
                      ? `${video.shortDescription.substring(0, 60)}...`
                      : video.shortDescription
                    }
                  </span>
                )}
              </div>
              <i className="fa-solid fa-plus add-icon"></i>
            </button>
          ))}
        </div>
      )}

      {showResults && searchResults.length === 0 && searchQuery.trim() && !isSearching && (
        <div className="search-no-results">
          <i className="fa-solid fa-search"></i>
          <p>No videos found for "{searchQuery}"</p>
          <small>Try different keywords</small>
        </div>
      )}
    </div>
  );
}